/* Hand-entered (quick add) notes and the add flow
 *
 * Moved verbatim from currencykeep_2026-08-03.html, lines 576-612.
 * The body below is byte-identical to the original; only the import
 * block above and the export block below were added. (Phase 1, Step 2.)
 */

import { BY_SID, CATALOG, GRADES, gradesOf, isCustomGrade } from './catalog.js'
import { republish } from './community.js'
import { localDate } from './local-date.js'
import { newHoldingId } from './mapper.js'
import { yearOf } from './note-filters.js'
import { render } from './render.js'
import { cat, save, state, toast } from './state.js'
import { HAS, storageSet } from './storage.js'

/* ---------- hand-entered (quick add) notes ---------- */
let CUSTOM=[];
function isCust(n){return !!(n&&n.cust);}
/* ── THE "OTHER" TYPE — his ruling of 2026-10-26: *"Custom referes to an
 * entry thats not input from the resources … Other is the specific type of
 * note."* A TYPE a custom entry carries in its `section`, never a catalogue
 * section (no row carries it), so it lives on custom notes alone. */
const OTHER_TYPE="Other";
function isOther(n){return !!(n&&n.cust&&n.section===OTHER_TYPE);}
/* ⚠ OTHER'S STEP 5, his ruling 6 (*"Other notes start at no grade instead
 * of VF20, if it checks out against how cards print the grade."*) and his A (*"Yes A is fine"* — a VF20 he tapped goes too, like
 * the automatic one): a note ENTERING Other — his pick or the AI's fill —
 * loses a VF20; any other grade stays. Leaving Other puts nothing back (his
 * B, *"Stay blank"*). The caller saves. Returns true when it cleared.
 * ⚠ HIS (b): a VF20 a slab LABEL gave stays — `gradeRead`, the grade the
 * label's fill stamped on the entry (suggest.js `applyReading`), private
 * inside `custom_entry` (db/009 projects four keys), kept when he taps that
 * same grade to confirm it, removed when he taps another (main.js). */
function enterOther(h){if(h&&h.grade==="VF20"&&cat(h.catId)?.gradeRead!=="VF20"){h.grade="";return true;}return false;}
function regCustom(n){BY_SID[n.sid]=n;}
async function loadCustom(){
  if(!HAS){CUSTOM=[];return;}
  try{const r=await window.storage.get("notevault:customnotes",false);CUSTOM=r&&r.value?JSON.parse(r.value):[];}catch(e){CUSTOM=[];}
  CUSTOM.forEach(regCustom);
}
async function saveCustom(){try{if(HAS)await storageSet("notevault:customnotes",JSON.stringify(CUSTOM),false);}catch(e){console.error(e);}}
function sectionList(){return [...new Set(CATALOG.map(n=>n.section))].sort();}
/* ⚠ THE BLANK ENTRY IS ONE FUNCTION SINCE 7.3(c), 2026-09-23: `quickAdd`
 * made it inline from Phase 1; the "Not the right note" button makes a
 * SECOND one (his ruling 2: it RECREATES a custom entry rather than
 * undoing), and two copies of the shape is how one drifts. Registered
 * as it is made, exactly as before. */
function blankCustom(){
  const sid="CUSTOM:"+Date.now()+Math.floor(Math.random()*1000);
  const n={fr:"",section:"Custom entry",denom:"",desc:"",gl:GRADES.slice(),g:GRADES.map(()=>null),st:null,fd:"",sid:sid,cust:true};
  CUSTOM.push(n);regCustom(n);
  return n;
}
function quickAdd(){
  const n=blankCustom();const sid=n.sid;
  /* ⚠ STOP D (2026-10-03): the DEVICE's calendar date, not the UTC one —
   * the UTC slice dated an evening add tomorrow (local-date.js). */
  const h={id:newHoldingId(),catId:sid,grade:"VF20",cost:"",acq:localDate(),memo:"",year:""};
  state.collection.push(h);
  saveCustom();save();
  state.sel=h.id;state.screen="holding";render();
}
// drop a quick-add entry the user opened but never filled in
/* ⚠ An Other note is kept — his words, 2026-10-26: *"An Other note with
 * only its Type set must not be deleted as empty."* The Type is the one
 * thing the collector set, and it is not empty. */
function pruneEmptyCustom(){
  const h=state.collection.find(x=>x.id===state.sel);if(!h)return;
  const n=cat(h.catId);if(!isCust(n))return;
  if(!n.fr&&!n.denom&&!h.cost&&!h.appr&&!h.memo&&!h.year&&!h.hasFront&&!h.hasBack&&!isOther(n)){
    state.collection=state.collection.filter(x=>x.id!==h.id);
    CUSTOM=CUSTOM.filter(c=>c.sid!==n.sid);delete BY_SID[n.sid];
    save();saveCustom();
  }
}

/* ---------- add flow ---------- */
function startAdd(catId){
  const n=cat(catId);const gs=gradesOf(n);const firstGrade=gs.find(g=>n.g[gs.indexOf(g)]!=null)||gs[0]||"VF20";
  /* ⚠ THE YEAR FROM `yearOf` HERE TOO — HIS RULING 2026-09-23, the same
   * gap as the conversion's and not a new behaviour: a note added from
   * Resources whose year lives in `series` (5,936 rows since the 09-20
   * recovery) started with no year while its hero printed one. The
   * holding is new, so the year is empty by construction. */
  const y=yearOf("",n);
  const h={id:newHoldingId(),catId:n.sid,grade:firstGrade,cost:"",acq:localDate(),memo:"",year:(y?String(y):"")};  /* STOP D: the device's date */
  state.collection.push(h);save();republish();state.sel=h.id;state.screen="holding";render();toast("Added to your vault");
}

/* ── THE ONE ADDITION IN STEP 2 ──────────────────────────────────────
 * Everything above this line was moved verbatim. This function is new.
 * See scripts/extract.mjs for why it has to exist.
 */
function dropCustom(sid) {
  CUSTOM = CUSTOM.filter(c => c.sid !== sid)
  delete BY_SID[sid]
}

/* ── STEP 7.3, 2026-09-23: THE CONVERSION IS ONE FUNCTION ─────────────
 *
 * A quick-add note becomes a real catalogue row here, and ONLY here.
 * This body was the `#cfr` change handler in `main.js` from Phase 1 until
 * today, inline; it moved so that the slab lookup (7.3) can run the SAME
 * conversion a typed Fr runs — never a second copy of it, his ruling
 * 16's reasoning. `grade-check` §4 drives the typed handler and is the
 * control that the move changed nothing.
 *
 * What it does, in the order the handler did it: the holding points at
 * the row; the year fills if empty; the grade re-picks onto a tab the row
 * has — ⚠ a CUSTOM grade is on every note and must NOT be re-picked
 * (step 3.3, 2026-09-15); the quick-add entry is dropped; save, the
 * custom list, republish.
 *
 * ⚠ IT DOES NOT RENDER AND DOES NOT TOAST. The typed handler speaks
 * because the collector is on the screen; the boot sweep (7.3's second
 * landing site) lands with them anywhere in the app, and a toast about a
 * note they cannot see is noise. Each caller decides; the text is ONE
 * constant so there is one copy of it.
 */
const MATCHED_TOAST = (fr) => "Matched Fr. "+fr+" — catalog values added"
function convertToRow(h, t){
  const n=cat(h.catId);const old=n.sid;h.catId=t.sid;
  /* ⚠ THE YEAR COMES FROM `yearOf` — HIS RULING 2026-09-23, A FIX AND NOT
   * A BEHAVIOUR CHANGE: the 7.3(b) render converted `2156-C★` and left
   * the year EMPTY while the hero printed *Series 1934-D*, because
   * `noteYear` scans `desc` and the row's year has lived in `series`
   * since the 09-20 recovery. `yearOf` is the source the sort uses, and
   * an empty own-year is passed so the row decides. Still only if empty:
   * a typed year is the collector's. */
  if(!h.year){const y=yearOf("",t);if(y)h.year=String(y);}
  /* A custom grade is on every note, so an Fr# match must not re-pick it
   * the way it re-picks a tab the new note lacks (step 3.3, 2026-09-15). */
  const gs=gradesOf(t);if(!isCustomGrade(h.grade)&&gs.indexOf(h.grade)<0)h.grade=gs.find(g=>t.g[gs.indexOf(g)]!=null)||gs[0]||"VF20";
  dropCustom(old);
  save();saveCustom();republish();
}

/* ── STEP 7.3(c), 2026-09-23: "NOT THE RIGHT NOTE" — HIS RULING 2 ──────
 *
 * *"One tap turns the note back into a blank custom note, keeping the
 * photo, grade, notes and anything I typed, and clears the ✨. It
 * recreates a custom entry rather than undoing — the typed path is
 * untouched."*
 *
 * So: a NEW blank entry, the holding pointed at it, and nothing the
 * collector did touched — the photo refs, the cost, the memo, the
 * qualifier, a typed year, a tapped grade all stay.
 *
 * ⚠⚠ WHAT THE CONVERSION ITSELF FILLED IS CLEARED — his detail,
 * 2026-09-23: *"the year filled by the conversion (from the wrong row's
 * series) should be cleared on revert — I didn't type it, and it belongs
 * to the wrong note. A year I typed stays. Same for anything else the
 * conversion itself filled."* `filled` is what the arrival recorded on
 * the identity marker at the moment it converted (`{ year, grade }`,
 * each the value it wrote or null); a field is cleared ONLY if it still
 * holds that exact value — a year retyped since is theirs and stays. The
 * grade goes back to `quickAdd`'s `VF20`, the blank entry's own
 * automatic grade. ⚠ The caller passes `grade: null` when the photo has
 * since filled the grade with its own ✨ — that value is the label's,
 * not the conversion's, and stays.
 *
 * ⚠ And a grade key the blank entry's seven tabs do not carry (a row's
 * `Fr2` or `Gd4`, say) falls back to `VF20` for a plainer reason: a key
 * no tab shows would leave the grade row with nothing selected. A CUSTOM
 * grade is on every note and is kept.
 *
 * ⚠ IT DOES NOT CLEAR THE MARKER, RENDER OR TOAST — the caller does. It
 * does not know about `state.pending` and must not: the map stays
 * `pending.js`'s. ~~`save · saveCustom · republish`~~ `saveCustom · save ·
 * republish` because the row a stranger sees has changed.
 * ⚠⚠ THE ENTRY FIRST — problem 2 of his walk of `8ff48b00` (2026-10-28),
 * his go: signed in, each save syncs BOTH halves, and the holding saved
 * first pointed at an entry the custom half did not have yet — the mapper
 * refuses that (`mapper.js:393`), with no status, so the offline bar and
 * the "not saved" toast showed with the network fine. `quickAdd`'s order;
 * `search-check` H7, H8.
 */
/* ══ STEP 7.5 — ONE CATALOGUE ROW TO ANOTHER, ON THE COLLECTOR'S TAP ══
 * "Update this note" (suggest.js `updateToDifferent`). ⚠ NOT
 * `convertToRow`: that converts a CUSTOM entry and drops it from BY_SID,
 * and dropping a catalogue sid would delete the catalogue's own row. The
 * grade and year are decided by the caller under his ruling (the tap is
 * consent, so both are SET, the year even over a filled one); cost, memo
 * and photos are not touched. The YEAR is always set: the new row's series
 * year, or EMPTY when the row has none (his review, 2026-09-25).
 * ⚠ THE QUALIFIER, HIS RULING THE SAME DAY: it follows the grade — the
 * reading's EPQ/PPQ, or OFF when none was read, "since it's printed with
 * the grade on the label". This is the one place outside the collector's
 * own toggle that turns it off, and it is here, not in suggest.js,
 * because it is the collector's TAP: the AI's fill still only turns it
 * on (his 2026-09-21 ruling, pending-check §6d). ⚠ The memo is not
 * touched (his "cost and memo stay"), so a memo that still carries the
 * letters keeps `qualifierOn` reading ON — `memo.js`'s compatibility
 * read, and the carry-over already emptied his. */
function switchToRow(h,t,{grade,year,qualifier}){
  if(isCust(cat(h.catId))||isCust(t))return false;
  h.catId=t.sid;h.grade=grade;h.year=year;h.qualifier=!!qualifier;
  save();republish();
  return true;
}

/* ⚠ OTHER'S STEP 4: the new entry is stamped `reverted` (inside
 * `custom_entry`, private — `db/009` projects four keys), so a hand pick of
 * Other on it sticks (his answer 2: *"Only a pick of Other I make by hand
 * after a photo converted the note sticks."*) and the AI never fills Other
 * on it (his ruling, suggest.js `syncNoFrFill`). Saved with the entry.
 * ⚠ THE `gradeRead` GAP, HIS RULING B OF 2026-10-29: *"at the revert — the
 * handler passes the ✨'s value and revertToCustom stamps gradeRead when the
 * kept grade is that value"* — `read` is the grade ✨'s value (main.js), so a
 * label's VF20 that filled the converted row is kept on Other after a reload,
 * his (b), as `suggest.js`'s fill stamps one on a custom entry. */
function revertToCustom(h,filled){
  const n=blankCustom();n.reverted=true;const f=filled||{};
  h.catId=n.sid;
  if(f.year!=null&&h.year===f.year)h.year="";
  if(f.grade!=null&&h.grade===f.grade)h.grade="VF20";
  if(!isCustomGrade(h.grade)&&GRADES.indexOf(h.grade)<0)h.grade="VF20";
  if(f.read!=null&&h.grade===f.read)n.gradeRead=f.read;
  saveCustom();save();republish();
  return n;
}

export {
  MATCHED_TOAST,
  OTHER_TYPE,
  convertToRow,
  revertToCustom,
  dropCustom,
  enterOther,
  isCust,
  isOther,
  loadCustom,
  pruneEmptyCustom,
  quickAdd,
  saveCustom,
  sectionList,
  startAdd,
  switchToRow,
}
